import { PortalTelemetryEvent } from '../../app/telemetry/portalTelemetryEvents';
import { PortalTelemetryScreen } from '../../app/telemetry/portalTelemetryScreens';
import { validationIssues } from '../../app/telemetry/validationTelemetry';
import { portalAction, portalScreenContext, trackPortalEvent } from '../../app/telemetry/portalTelemetry';
import { usePortalScreen } from '../../app/telemetry/usePortalTelemetry';
import { Box, Button, Grid, Paper, Step, StepLabel, Stepper } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import DeclarationAndPetition from './DeclarationAndPetition';
import { useAppDispatch, useAppSelector } from '../../app/store/configureStore';
import LoadingComponent from '../../app/layout/LoadingComponent';
import { ApiRequestStatus } from '../../app/enums/apiRequestStatus';
import { getPersonalDetailsAsync, updatePersonalDetailsStateAsync, updateVerifyPersonalDetailsAsync } from '../../app/slices/personSlice';
import {
  API_REQUEST_FULLFILLED, MESSAGE_DETAILS_SAVED, MESSAGE_SAVE_AND_CONTINUE, MESSAGE_NEXT, MESSAGE_SUBMIT,
  MESSAGE_BACK_TO_PREVIOUS_STEP, MESSAGE_NOT_A_VALUE, NO_FURTHER_ACTION_REQUIRED, MESSAGE_PROCESSING_DETAILS,
  SKIP_TO_UPLOAD, NOT_AUTHORISED_TO_ACCESS_SPA_MESSAGE
} from '../../app/utils/constant';
import { toast } from 'react-toastify';
import { PersonalDetailsDto } from '../../app/models/person/personalDetailsDto';
import { AddSupportingDocument } from './AddSupportingDocuments';
import MembershipTransferHeader from './MembershipTransferHeader';
import { membershipTransferValidationSchemas } from './common/membershipTransferValidationSchemas';
import TransferInformation from './TransferInformation';
import {
  getFamTransferDocumentsAsync, getFamTransferDetailsAsync, saveFamTransferDetailsAsync,
  submitFamTransferDetailsAsync, updateFamTransferDetailsAsync, basketAsync
} from '../../app/slices/famTransferSlice';
import { FamOnboardingStatus } from '../../app/models/staticData/famOnboardingStatus';
import { MemberTransferStatusCodeEnum } from '../../app/enums/memberTransferStatusCodeEnum';
import { MemberTransferStepEnum } from './common/memberTransferStepsEnum';
import { FAMOnboarding } from '../../app/models/membership/famOnboarding';
import MemberPostalAddress from '../../app/models/membership/MemberPostalAddress';
import MemberPersonalDetails from '../../app/models/membership/MemberPersonalDetails';
import { FAMDetails, LocationPreferences } from '../../app/models/membership/famDetails';
import { getFAMDetails, updateFAMDetails, addFAMDetails } from '../../app/slices/famSlice';
import LocationAndPreferencesDetails from '../../app/models/membership/LocationAndPreferencesDetails';
import { getPaymentRedirectAsync, getPaymentSummaryAsync } from '../../app/slices/paymentSlice';
import MembershipPaymentSummary from '../../app/models/membership/MembershipPaymentSummary';
import { PaymentRedirectProps } from '../../app/models/membership/paymentRedirectProps';
import { useMsal } from '@azure/msal-react';
import { UserClaims } from '../../app/enums/userClaims';
import Unauthorized from '../../app/errors/Unauthorized';
import { MemberType } from '../../app/enums/memberType';
import { MemberTransferDocumentTypeEnum } from './common/memberTransferDocumentTypeEnum';
import { SpaName } from '../../app/enums/spaName';
import { MembershipTypeEnum } from '../../app/enums/MembershipTypeEnum';
import { IdTokenClaims } from '../../app/models/account/idTokenClaims';
import { useReferenceData } from '../../app/customHooks/useReferenceData';
import { useGetCurrentMembershipType } from '../../app/customHooks/useGetCurrentMembershipType';
import { useGetFamTransferDetails } from '../../app/customHooks/useGetFamTransferDetails';
import { useGetFamDetails } from '../../app/customHooks/useGetFamDetails';
import { useGetFamOnboardingDetails } from '../../app/customHooks/useGetFamOnboardingDetails';
import { useGetPersonalDetails } from '../../app/customHooks/useGetPersonalDetails';
import { getPaymentIdFromBasketAsync } from '../../app/slices/shoppingBasketSlice';
import { formatDate } from '../../app/utils/util';

export default function MembershipTransferForm() {

  const { instance } = useMsal();
  const idTokenClaimsObject = (instance.getActiveAccount()?.idTokenClaims as unknown as IdTokenClaims);

  const steps = [MemberTransferStepEnum.PersonalDetails, MemberTransferStepEnum.PostalAddress, MemberTransferStepEnum.Location,
  MemberTransferStepEnum.TransferInformation, MemberTransferStepEnum.SupportingDocuments, MemberTransferStepEnum.DeclarationAndPetition];

  function getStepContent(step: number) {
    switch (step) {
      case 0:
        return <MemberPersonalDetails />
      case 1:
        return <MemberPostalAddress />
      case 2:
        return <LocationAndPreferencesDetails />
      case 3:
        return <TransferInformation />
      case 4:
        return <AddSupportingDocument documents={famTransferDocuments} famTransferDetails={famTransferDetails}
          unSavedCertificateChange={isUnsavedCertificateChange}
          certificateChange={() => { setIsUnsavedCertificateChange(false) }}
          unSavedExamChange={isUnsavedExamChange}
          examChange={() => { setIsUnsavedExamChange(false) }}
          unSavedCvChange={isUnsavedCvChange}
          cvChange={() => { setIsUnsavedCvChange(false) }}
          documentSubmittedCallback={documentSubmittedCallback} />
      case 5:
        return <DeclarationAndPetition />
      default:
        throw new Error('Unknown step');
    }
  }

  const [activeStep, setActiveStep] = useState(0);
  const [isUnsavedCertificateChange, setIsUnsavedCertificateChange] = useState(false);
  const [isUnsavedExamChange, setIsUnsavedExamChange] = useState(false);
  const [isUnsavedCvChange, setIsUnsavedCvChange] = useState(false);

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(membershipTransferValidationSchemas[activeStep]),
    defaultValues: {
      Title: '',
      FirstName: '',
      MiddleName: '',
      PreferredName: '',
      Surname: '',
      DateOfBirth: null,
      Gender: '',
      CountryOfNationality: '',
      PreferredPhoneNumber: '',
      Discipline: '',
      AreaOfPracticeMoreDetails: '',
      PostalAddressLines: '',
      PostalAddressCountry: '',
      LocationCountry: '',
      CountrySubdivision: '',
      SurgeonsScopeMagazine: '',
      TheSurgeonJournal: '',
      WhichRoyalCollegeAreYouTransferringFrom: '',
      NameOnCertificate: '',
      Certificate: '',
      Exam: '',
      CV: ''
    }
  });

  const { trigger, formState: { isDirty } } = methods;
  const formObj = methods.watch();

  const { accounts } = useMsal();

  useEffect(() => {
    if (accounts.length > 0 && accounts[0]?.idTokenClaims) {
      setPersonId(accounts[0].idTokenClaims[UserClaims.EXTENSION_PORTALCRM_PERSONID] as string);
    }
  }, [accounts]);

  const [personId, setPersonId] = useState('');

  const dispatch = useAppDispatch();

  const { staticDataStateStatus } = useReferenceData();
  const { currentMembershipTypeStatus } = useGetCurrentMembershipType();
  const { famTransferDetailsStatus } = useGetFamTransferDetails();
  const { personalDetailsStatus } = useGetPersonalDetails();
  const { famDetailsStatus } = useGetFamDetails();
  const { famOnboardingDetailsStatus } = useGetFamOnboardingDetails();

  const { countries, famMembershipTypes, famOnboardingStatuses } = useAppSelector(state => state.ReferenceData);
  const { personalDetails, updatePersonalDetailsStatus, updateVerifyPersonalDetailsStatus }
    = useAppSelector(state => state.personalDetail);
  const { famTransferDetails, famTransferDocuments, saveFamTransferDetailsStatus, submitFamTransferDetailsStatus,
    updateFamTransferDetailsStatus, basketId, basketIdStatus, famTransferDocumentsStatus
  } = useAppSelector(state => state.famTransfer);
  const { famDetails, currentMembershipType, addFamDetailsStatus, updateFamDetailsStatus } = useAppSelector(state => state.famDetails);
  const { famOnboardingDetails } = useAppSelector(state => state.famOnboarding);
  const { paymentRedirectStatus, paymentDetails, paymentSummaryStatus } = useAppSelector(state => state.payment);
  const { paymentId, paymentIdStatus } = useAppSelector(state => state.shoppingBasket);

  const [pageLoading, setPageLoading] = useState<boolean | undefined>(undefined);
  const [famOnboardingStatus, setFamOnboardingStatus] = useState<FamOnboardingStatus>();
  let supportingDocumentStepIndex = Object.values(MemberTransferStepEnum).indexOf(MemberTransferStepEnum.SupportingDocuments);

  useEffect(() => {
    if ((famOnboardingStatuses !== null && famOnboardingStatuses?.length > 0)
      && famTransferDetails?.famOnboardingStatusId && famTransferDetails?.famOnboardingStatusId !== null) {
      let currentStatus = famOnboardingStatuses?.find(x => x.id === famTransferDetails?.famOnboardingStatusId);
      setFamOnboardingStatus(currentStatus);

      if (currentStatus?.code === MemberTransferStatusCodeEnum.RequiredFurtherDetails) {
        setActiveStep(supportingDocumentStepIndex);
      }
    }
  }, [famTransferDetails, famOnboardingStatuses, famOnboardingStatus, supportingDocumentStepIndex]);

  useEffect(() => {
    if (personId !== "" && famTransferDetails !== null &&
      (famOnboardingStatuses !== null && famOnboardingStatuses.length > 0) &&
      famTransferDocumentsStatus !== ApiRequestStatus.Fulfilled &&
      (famOnboardingStatuses.find(x => x.id === famTransferDetails.famOnboardingStatusId)?.code === MemberTransferStatusCodeEnum.Pending
        || famOnboardingStatuses.find(x => x.id === famTransferDetails.famOnboardingStatusId)?.code === MemberTransferStatusCodeEnum.RequiredFurtherDetails)) {

      dispatch(getFamTransferDocumentsAsync());
    }
  }, [personId, famTransferDetails, famOnboardingStatuses, famTransferDocumentsStatus, dispatch]);

  function MapToPersonalDetails() {
    const data: PersonalDetailsDto = {
      ...personalDetails, id: personId,
      titleId: formObj.Title as string,
      middleName: personalDetails?.middleName === formObj.MiddleName ? '' : formObj.MiddleName as string,
      prefferedName: formObj.PreferredName as string,
      genderId: formObj.Gender as string,
      nationalityId: formObj.CountryOfNationality as string,
      dateOfBirth: (formObj.DateOfBirth && formatDate(new Date(formObj.DateOfBirth))) ?? '',
      mobile: formObj.PreferredPhoneNumber as string,
      disciplineId: formObj.Discipline as string,
      areaOfPracticeId: formObj.AreaOfPracticeMoreDetails === MESSAGE_NOT_A_VALUE ? '' : formObj.AreaOfPracticeMoreDetails as string
    }
    return data;
  }

  function MapToPostalAddressDetails() {
    const data: PersonalDetailsDto = {
      ...personalDetails, id: personId,
      postalAddress: formObj.PostalAddressLines as string,
      postalAddressCountryId: formObj.PostalAddressCountry as string,
      dateOfBirth: (personalDetails?.dateOfBirth && formatDate(new Date(personalDetails?.dateOfBirth))) ?? ''
    }
    return data;
  }

  function MapToLocationDetails() {
    const data: PersonalDetailsDto = {
      ...personalDetails, id: personId,
      locationCountryId: formObj.LocationCountry as string,
      locationCountrySubdivisionId: formObj.CountrySubdivision === MESSAGE_NOT_A_VALUE ? '' : formObj.CountrySubdivision as string,
      dateOfBirth: (personalDetails?.dateOfBirth && formatDate(new Date(personalDetails?.dateOfBirth))) ?? ''
    }
    return data;
  }

  function MapToFAMOnboarding() {
    const data: FAMOnboarding = {
      famExamId: famTransferDetails?.famExamId ? famTransferDetails?.famExamId : null,
      famOnboardingStatusId: famTransferDetails?.famOnboardingStatusId ? famTransferDetails?.famOnboardingStatusId : '',
      famEntryMethodId: famTransferDetails?.famEntryMethodId ? famTransferDetails?.famEntryMethodId : '',
      famMembershipTypeId: famTransferDetails?.famMembershipTypeId ? famTransferDetails?.famMembershipTypeId : '',
      membershipAwardId: famTransferDetails?.membershipAwardId ? famTransferDetails?.membershipAwardId : '',
      ratificationDate: famTransferDetails?.ratificationDate ? famTransferDetails?.ratificationDate : null,
      declarationDate: famTransferDetails?.declarationDate ? famTransferDetails?.declarationDate : null,
      electionPetitionDate: famTransferDetails?.electionPetitionDate ? famTransferDetails?.electionPetitionDate : null,
      personId: personId,
      royalCollegeId: formObj.WhichRoyalCollegeAreYouTransferringFrom as string,
      nameOnCert: formObj.NameOnCertificate as string
    }
    return data;
  }

  const MapToFamDetails = () => {
    let famDetailsObj = {
      ...famDetails,
      scopePreferenceId: formObj.SurgeonsScopeMagazine as string,
      surgeonJournalPreferenceId: formObj.TheSurgeonJournal as string,
      personId: personId
    };

    return famDetailsObj;
  }

  const savePublicationPreferences = (locationPreferencesDto: LocationPreferences) => {
    if (famDetails?.famDetailsId && famDetails?.famDetailsId !== null) {
      dispatch(updateFAMDetails(locationPreferencesDto))
        .then((updateFAMDetailsResponse: any) => {
          if (updateFAMDetailsResponse.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
            dispatch(getFAMDetails(personId)).then((getFamDetailsResponse: any) => {
              if (getFamDetailsResponse.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                dispatch(updatePersonalDetailsStateAsync(MapToLocationDetails())).then((personalDetailsResponse: any) => {
                  if (personalDetailsResponse.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                    dispatch(getPersonalDetailsAsync(personId)).then(() => {
                      setActiveStep(prevActiveStep => prevActiveStep + 1);
                      toast.success(MESSAGE_DETAILS_SAVED);
                    });
                  }
                });
              }
            });
          }
        });
    }
    else if (!famDetails?.famDetailsId || famDetails?.famDetailsId === null) {
      let famDetailsObj: FAMDetails = locationPreferencesDto;
      famDetailsObj.membershipTypeId = famMembershipTypes?.find(x => x.code === MemberType.MEMBER)?.id;

      dispatch(addFAMDetails(locationPreferencesDto))
        .then((addFAMDetailsResponse: any) => {
          if (addFAMDetailsResponse.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
            dispatch(getFAMDetails(personId)).then((getFamDetailsResponse: any) => {
              if (getFamDetailsResponse.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                dispatch(updatePersonalDetailsStateAsync(MapToLocationDetails())).then((personalDetailsResponse: any) => {
                  if (personalDetailsResponse.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                    dispatch(getPersonalDetailsAsync(personId)).then(() => {
                      setActiveStep(prevActiveStep => prevActiveStep + 1);
                      toast.success(MESSAGE_DETAILS_SAVED);
                    });
                  }
                });
              }
            });
          }
        });
    }
  }

  const savePersonalDetails = (personalDetailsDto: PersonalDetailsDto) => {
    dispatch(updatePersonalDetailsStateAsync(personalDetailsDto)).then((response: any) => {
      if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
        dispatch(getPersonalDetailsAsync(personId)).then(() => {
          setActiveStep(prevActiveStep => prevActiveStep + 1);
          toast.success(MESSAGE_DETAILS_SAVED);
        });
      }
    });
  }

  const updateTransferInformation = () => {
    dispatch(updateFamTransferDetailsAsync(MapToFAMOnboarding())).then((response: any) => {
      if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
        dispatch(getFamTransferDetailsAsync()).then(() => {
          setActiveStep(prevActiveStep => prevActiveStep + 1);
          toast.success(MESSAGE_DETAILS_SAVED);
        });
      }
    });
  }

  const savefamTransferDetails = () => {
    if (famTransferDetails?.famOnboardingStatusId &&
      famTransferDetails?.famOnboardingStatusId !== null &&
      famTransferDetails?.famOnboardingStatusId !== '') {
      updateTransferInformation();
    }
    else {
      dispatch(saveFamTransferDetailsAsync(MapToFAMOnboarding())).then((response: any) => {
        if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
          dispatch(getFamTransferDetailsAsync()).then(() => {
            setActiveStep(prevActiveStep => prevActiveStep + 1);
            toast.success(MESSAGE_DETAILS_SAVED);
          });
        };
      })
    }
  }

  const submitMemberTransferDetails = () => {
    let famExamId = famTransferDetails?.famExamId ? famTransferDetails?.famExamId : '';
    dispatch(submitFamTransferDetailsAsync(famExamId)).then((response: any) => {
      if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
        dispatch(getFamTransferDetailsAsync()).then(() => {
          setActiveStep(prevActiveStep => prevActiveStep + 1);
          toast.success(MESSAGE_DETAILS_SAVED);
        });
      };
    })
  }

  const documentSubmittedCallback = () => {
    dispatch(getFamTransferDocumentsAsync());
  }

  const handleNext = async () => {
    const certificateDocument = famTransferDocuments?.find(x => x.tags?.FILE_TYPE === MemberTransferDocumentTypeEnum.CERTIFICATE);
    if (certificateDocument && formObj.Certificate && formObj.Certificate !== SKIP_TO_UPLOAD && typeof formObj.Certificate === 'object' && (formObj.Certificate as any).uri === undefined) {
      setIsUnsavedCertificateChange(true);
      return;
    } else {
      setIsUnsavedCertificateChange(false);
    }

    const examDocument = famTransferDocuments?.find(x => x.tags?.FILE_TYPE === MemberTransferDocumentTypeEnum.EXAM);
    if (examDocument && formObj.Exam && formObj.Exam !== SKIP_TO_UPLOAD && typeof formObj.Exam === 'object' && (formObj.Exam as any).uri === undefined) {
      setIsUnsavedExamChange(true);
      return;
    } else {
      setIsUnsavedExamChange(false);
    }

    const curriculumVitaeDocument = famTransferDocuments?.find(x => x.tags?.FILE_TYPE === MemberTransferDocumentTypeEnum.CV);
    if (curriculumVitaeDocument && formObj.CV && formObj.CV !== SKIP_TO_UPLOAD && typeof formObj.CV === 'object' && (formObj.CV as any).uri === undefined) {
      setIsUnsavedCvChange(true);
      return;
    } else {
      setIsUnsavedCvChange(false);
    }

    const isStepValid = await trigger();

    if (!isStepValid) {
      trackPortalEvent(portalScreenContext(PortalTelemetryScreen.MembershipTransferForm),
        PortalTelemetryEvent.ValidationBlocked, {
        step: steps[activeStep],
        validationErrors: validationIssues(PortalTelemetryScreen.MembershipTransferForm, methods.getFieldState)
      });
    }

    if (isStepValid) {
      if (isDirty === true) {
        switch (activeStep) {
          case 0:
            savePersonalDetails(MapToPersonalDetails());
            break;
          case 1:
            savePersonalDetails(MapToPostalAddressDetails());
            break;
          case 2:
            savePublicationPreferences(MapToFamDetails());
            break;
          case 3:
            savefamTransferDetails();
            break;
          case 4:
            setActiveStep(prevActiveStep => prevActiveStep + 1);
            break;
          case 5:
            submitMemberTransferDetails();
            break;
          default:
            return "not a valid step";
        }
      }
      else if (isDirty === false) {
        switch (activeStep) {
          case 0:
            dispatch(updateVerifyPersonalDetailsAsync()).then((response: any) => {
              if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                toast.success(MESSAGE_DETAILS_SAVED);
              }
            });
            break;
        }
        setActiveStep(prevActiveStep => prevActiveStep + 1);
      }
    }
  }

  useEffect(() => {
    if (!paymentDetails && paymentId !== '' && paymentId !== null) {
      paymentId && dispatch(getPaymentSummaryAsync(paymentId));
    }
  }, [paymentDetails, paymentId, dispatch]);

  useEffect(() => {
    if (famTransferDetails !== null && famTransferDetails?.famOnboardingStatusId &&
      famTransferDetails?.famOnboardingStatusId !== null &&
      famTransferDetails?.famOnboardingStatusId !== '' &&
      famOnboardingStatus && famOnboardingStatus?.code === MemberTransferStatusCodeEnum.Approved &&
      famDetails && countries && famMembershipTypes &&
      (basketId === '' || basketId === null)) {
      dispatch(basketAsync());
    }
  }, [famTransferDetails, famOnboardingStatus, famDetails, paymentDetails, basketId,
    countries, famMembershipTypes, dispatch]);

  useEffect(() => {
    if (basketId !== '' && basketId !== null) {
      dispatch(getPaymentIdFromBasketAsync(basketId));
    }
  }, [basketId, dispatch]);

  const paymentOnClickHandler = () => {
    if (paymentId && paymentId !== null) {
      setPageLoading(true);
      let paymentRedirectProps: PaymentRedirectProps = {
        cancelUrl: `${String(process.env.REACT_APP_REDIRECT_URI)}${String(process.env.REACT_APP_MEMBERSHIP_TRANSFER_URI)}/${window.location.search}&cancelPayment=true`,
        paymentId: paymentId,
        successUrl: `${String(process.env.REACT_APP_REDIRECT_URI)}${String(process.env.REACT_APP_PAYMENT_SUCCESS_URI)}/${window.location.search}&retry-url=${String(process.env.REACT_APP_MEMBERSHIP_TRANSFER_URI)}`
      };
      dispatch(getPaymentRedirectAsync(paymentRedirectProps)).then((paymentRedirectResponse: any) => {
        window.location.replace(paymentRedirectResponse.payload);
      });
    }
  }

  async function _handleBack() {
    setActiveStep(activeStep - 1);
  }

  const [width, setWidth] = useState<number>(window.innerWidth);

  const handleWindowSizeChange = () => {
    setWidth(window.innerWidth);
  }

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    }
  }, []);

  const isMobile = width <= 768;

  usePortalScreen(portalScreenContext(PortalTelemetryScreen.MembershipTransferForm), steps[activeStep], !((pageLoading === true || staticDataStateStatus === ApiRequestStatus.Pending
    || updatePersonalDetailsStatus === ApiRequestStatus.Pending || updateFamTransferDetailsStatus === ApiRequestStatus.Pending
    || updateFamDetailsStatus === ApiRequestStatus.Pending || paymentRedirectStatus === ApiRequestStatus.Pending
    || paymentSummaryStatus === ApiRequestStatus.Pending || basketIdStatus === ApiRequestStatus.Pending
    || paymentIdStatus === ApiRequestStatus.Pending || saveFamTransferDetailsStatus === ApiRequestStatus.Pending
    || addFamDetailsStatus === ApiRequestStatus.Pending || submitFamTransferDetailsStatus === ApiRequestStatus.Pending
    || currentMembershipTypeStatus === ApiRequestStatus.Pending || famTransferDetailsStatus === ApiRequestStatus.Pending
    || famTransferDocumentsStatus === ApiRequestStatus.Pending || famDetailsStatus === ApiRequestStatus.Pending
    || personalDetailsStatus === ApiRequestStatus.Pending || famOnboardingDetailsStatus === ApiRequestStatus.Pending
    || updateVerifyPersonalDetailsStatus === ApiRequestStatus.Pending)));

  if ((famOnboardingDetails !== null && famOnboardingDetails.length > 0) ||
    idTokenClaimsObject.extension_portalcrm_isFellowOnboarding === true ||
    idTokenClaimsObject.extension_portalcrm_isMemberOnboarding === true) {
    toast.error(NO_FURTHER_ACTION_REQUIRED);
    return <Unauthorized message={NO_FURTHER_ACTION_REQUIRED} />
  }

  if (currentMembershipType?.code === MembershipTypeEnum.MEMBER ||
    currentMembershipType?.code === MembershipTypeEnum.FELLOW) {
    const errorMessage = `${NOT_AUTHORISED_TO_ACCESS_SPA_MESSAGE.replace('##SPA_MESSAGE##',
      `Member transfer as you're Member or Fellow`)}`
    toast.error(errorMessage);
    return <Unauthorized message={errorMessage} />
  }

  if (pageLoading === true || staticDataStateStatus === ApiRequestStatus.Pending
    || updatePersonalDetailsStatus === ApiRequestStatus.Pending || updateFamTransferDetailsStatus === ApiRequestStatus.Pending
    || updateFamDetailsStatus === ApiRequestStatus.Pending || paymentRedirectStatus === ApiRequestStatus.Pending
    || paymentSummaryStatus === ApiRequestStatus.Pending || basketIdStatus === ApiRequestStatus.Pending
    || paymentIdStatus === ApiRequestStatus.Pending || saveFamTransferDetailsStatus === ApiRequestStatus.Pending
    || addFamDetailsStatus === ApiRequestStatus.Pending || submitFamTransferDetailsStatus === ApiRequestStatus.Pending
    || currentMembershipTypeStatus === ApiRequestStatus.Pending || famTransferDetailsStatus === ApiRequestStatus.Pending
    || famTransferDocumentsStatus === ApiRequestStatus.Pending || famDetailsStatus === ApiRequestStatus.Pending
    || personalDetailsStatus === ApiRequestStatus.Pending || famOnboardingDetailsStatus === ApiRequestStatus.Pending
    || updateVerifyPersonalDetailsStatus === ApiRequestStatus.Pending) {
    return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
  }

  return (
    <FormProvider {...methods}>
      <MembershipTransferHeader famOnboardingStatus={famOnboardingStatus} />
      <br></br>
      {(famOnboardingStatus === undefined || famOnboardingStatus.code === MemberTransferStatusCodeEnum.Pending
        || famOnboardingStatus.code === MemberTransferStatusCodeEnum.RequiredFurtherDetails) &&
        <>
          <Stepper activeStep={activeStep} orientation={isMobile === true ? "vertical" : "horizontal"}>
            {steps.map((label) => {
              const stepProps = {};
              const labelProps = {};
              return (
                <Step key={label} {...stepProps}>
                  <StepLabel {...labelProps}>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>

          <br></br>
          {((activeStep !== 0 && (famOnboardingStatus === undefined || famOnboardingStatus.code !== MemberTransferStatusCodeEnum.RequiredFurtherDetails)) ||
            (activeStep > supportingDocumentStepIndex && famOnboardingStatus && famOnboardingStatus.code === MemberTransferStatusCodeEnum.RequiredFurtherDetails)) && (
              <Button variant="contained" onClick={portalAction(portalScreenContext(PortalTelemetryScreen.MembershipTransferForm), `${PortalTelemetryScreen.MembershipTransferForm}.onClick._handleBack`, _handleBack)}>
                {MESSAGE_BACK_TO_PREVIOUS_STEP}
              </Button>
            )}

          {activeStep === steps.length ? (
            <></>
          ) : (
            <>
              {getStepContent(activeStep)}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Grid container columnSpacing={12}>

                  <Grid size={{ xs: 12 }}></Grid>

                  <Grid size={{ xs: 11 }} sx={{ display: 'flex', justifyContent: 'end' }}>
                    <Button variant="contained" color="primary" onClick={portalAction(portalScreenContext(PortalTelemetryScreen.MembershipTransferForm), `${PortalTelemetryScreen.MembershipTransferForm}.onClick.handleNext`, handleNext)}>
                      {activeStep !== supportingDocumentStepIndex &&
                        (activeStep === steps.length - 1 ? MESSAGE_SUBMIT : (isDirty === true ? MESSAGE_SAVE_AND_CONTINUE : MESSAGE_NEXT))}
                      {(activeStep === supportingDocumentStepIndex) && MESSAGE_NEXT}
                    </Button>
                  </Grid>

                  <Grid size={{ xs: 1 }}></Grid>

                </Grid>
              </Box>
            </>
          )}
        </>
      }

      {famOnboardingStatus?.code === MemberTransferStatusCodeEnum.Approved && famDetails &&
        countries && famMembershipTypes &&
        <>
          <br></br>
          <br></br>
          <Box sx={{ minWidth: "100%" }} component={Paper}>

            <Grid container rowSpacing={4}>
              <MembershipPaymentSummary basketId={basketId} paymentOnClickHandler={paymentOnClickHandler}
                retryUrl={process.env.REACT_APP_MEMBERSHIP_TRANSFER_URI} SpaName={SpaName.MEMBER_TRANSFER}
                membershipType={famTransferDetails !== null ? famMembershipTypes.find(x => x.id === famTransferDetails.famMembershipTypeId)?.value : ''} />
            </Grid>
          </Box>
        </>
      }
    </FormProvider >
  );
};
